import { type FC, type ReactNode } from "react";
import {
  Card,
  Box,
  Grid,
  Typography,
  Stack,
  CardContent,
  Skeleton,
} from "@mui/material";
import { useIsMobile } from "@/shared/hooks";
import {
  BarChart,
  type BarChartData,
} from "@/partner/shared/components/BarChart";
import {
  AnalyticsCalendarInterval,
  type CalendarIntervalValue,
} from "@/partner/shared/components/AnalyticsCalendarInterval";
import { ErrorMessage } from "@/shared/components";

interface BarChartCardProps {
  title: string;
  chart?: BarChartData;
  loading?: boolean;
  /**
   * Ошибка от useQuery этого виджета — показывается вместо графика, чтобы
   * пустой график не читался как «нет данных» (спека, раздел 5).
   */
  error?: unknown;
  chartHeight?: number;
  dateFrom?: string;
  dateTo?: string;
  onChangeCalendarInterval?: (value: CalendarIntervalValue) => void;
  withWeek?: boolean;
  withHour?: boolean;
  withMinute?: boolean;
  withYear?: boolean;
  downloadButton?: ReactNode;
  renderLabel?: (value: string) => string;
  renderTrickY?: (value: number) => string | number;
}

export const BarChartCard: FC<BarChartCardProps> = ({
  title,
  chart,
  loading,
  error,
  chartHeight = 350,
  dateFrom,
  dateTo,
  onChangeCalendarInterval,
  withWeek,
  withHour,
  withMinute,
  withYear,
  downloadButton,
  renderLabel = (v) => v,
  renderTrickY = (v) => v,
}) => {
  const isMobile = useIsMobile();

  return (
    <Card>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h5" noWrap>
              {title}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent={isMobile ? "flex-start" : "flex-end"}
            size={{ xs: 12, md: 6 }}
          >
            <Stack direction="row" spacing={1}>
              {onChangeCalendarInterval && (
                <AnalyticsCalendarInterval
                  value={chart?.calendarInterval}
                  onChange={onChangeCalendarInterval}
                  withWeek={withWeek}
                  withHour={withHour}
                  withMinute={withMinute}
                  withYear={withYear}
                />
              )}
              {downloadButton}
            </Stack>
          </Grid>
        </Grid>
      </Box>
      <CardContent sx={{ pt: 0 }}>
        {error ? (
          <ErrorMessage error={error} />
        ) : loading || !chart ? (
          <Skeleton variant="rectangular" height={chartHeight} />
        ) : (
          <BarChart
            chart={chart}
            renderLabel={renderLabel}
            renderTrickY={renderTrickY}
            dateFrom={dateFrom}
            dateTo={dateTo}
            chartHeight={chartHeight}
          />
        )}
      </CardContent>
    </Card>
  );
};
